import React, { useState, useMemo } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { X, MagnifyingGlass, Check } from '@phosphor-icons/react';
import MBSetupBadge, { MB_SETUPS } from './MBSetupBadge';
import { useAuth } from '../contexts/AuthContext';
import { maskPhone, canViewFullPhone } from '../utils/phoneMask';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Bulk MB Setup Assignment Modal
 * Props:
 *  - open: boolean
 *  - onClose: fn
 *  - clients: [{ id, name, phone, mb_setup, unread_count? }]  // withdrawal clients
 *  - actorId: current user id
 *  - onDone: fn(updatedIds, mbSetup)  // called after successful bulk update
 */
const BulkAssignMBModal = ({ open, onClose, clients, actorId, onDone }) => {
  const { user } = useAuth();
  const hasFullPhoneAccess = canViewFullPhone(user);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [targetMB, setTargetMB] = useState(MB_SETUPS[0]);
  const [saving, setSaving] = useState(false);
  const [filterMB, setFilterMB] = useState('all'); // filter existing MB to show only some clients

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (clients || []).filter((c) => {
      if (c.is_guest || (c.phone && c.phone.startsWith('GUEST_'))) return false;
      const matchesSearch =
        !q ||
        (c.name || '').toLowerCase().includes(q) ||
        (c.phone || '').includes(q);
      const matchesMB =
        filterMB === 'all' ||
        (filterMB === 'unassigned' ? !c.mb_setup : c.mb_setup === filterMB);
      return matchesSearch && matchesMB;
    });
  }, [clients, search, filterMB]);

  if (!open) return null;

  const toggle = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllVisible = () => {
    const next = new Set(selectedIds);
    filtered.forEach((c) => next.add(c.id));
    setSelectedIds(next);
  };

  const clearAll = () => setSelectedIds(new Set());

  const handleApply = async (mbValue) => {
    if (selectedIds.size === 0) {
      toast.error('Select at least one client');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/clients/mb-setup/bulk`, {
        customer_ids: Array.from(selectedIds),
        mb_setup: mbValue,
        actor_id: actorId,
      });
      toast.success(
        mbValue
          ? `Assigned ${res.data.modified} client(s) to ${mbValue}`
          : `Cleared MB tag for ${res.data.modified} client(s)`
      );
      onDone && onDone(Array.from(selectedIds), mbValue);
      setSelectedIds(new Set());
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk assign failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="bulk-mb-modal"
    >
      <div
        className="bg-wa-panel rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b flex items-center justify-between bg-wa-panel border-l-4 border-[#008069] text-wa-text-primary">
          <div>
            <h2 className="text-lg font-bold">Bulk Assign MB Setup</h2>
            <p className="text-xs opacity-90">
              {selectedIds.size} selected · {(clients || []).length} total
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-white/20"
            data-testid="bulk-mb-close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Search + Filter */}
        <div className="px-5 py-3 border-b space-y-2">
          <div className="relative">
            <MagnifyingGlass
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-wa-text-secondary"
            />
            <input
              type="text"
              placeholder="Search by name or phone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 rounded-lg bg-wa-hover focus:bg-wa-panel focus:ring-2 focus:ring-teal-500 outline-none text-sm"
              data-testid="bulk-mb-search"
            />
          </div>
          <div className="flex gap-1.5 overflow-x-auto">
            <button
              onClick={() => setFilterMB('all')}
              className={`text-[11px] px-2.5 py-1 rounded-full font-semibold whitespace-nowrap ${
                filterMB === 'all'
                  ? 'bg-teal-600 text-white'
                  : 'bg-wa-hover text-wa-text-primary'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilterMB('unassigned')}
              className={`text-[11px] px-2.5 py-1 rounded-full font-semibold whitespace-nowrap ${
                filterMB === 'unassigned'
                  ? 'bg-teal-600 text-white'
                  : 'bg-wa-hover text-wa-text-primary'
              }`}
            >
              Unassigned
            </button>
            {MB_SETUPS.map((mb) => (
              <button
                key={mb}
                onClick={() => setFilterMB(mb)}
                className={`text-[11px] px-2.5 py-1 rounded-full font-semibold whitespace-nowrap ${
                  filterMB === mb
                    ? 'bg-teal-600 text-white'
                    : 'bg-wa-hover text-wa-text-primary'
                }`}
              >
                {mb}
              </button>
            ))}
          </div>
          <div className="flex justify-between text-xs">
            <button
              onClick={selectAllVisible}
              className="text-teal-600 font-semibold hover:underline"
              data-testid="bulk-mb-select-all"
            >
              Select all visible ({filtered.length})
            </button>
            {selectedIds.size > 0 && (
              <button
                onClick={clearAll}
                className="text-red-500 font-semibold hover:underline"
                data-testid="bulk-mb-clear-sel"
              >
                Clear selection
              </button>
            )}
          </div>
        </div>

        {/* Client list */}
        <div className="flex-1 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="text-center py-10 text-wa-text-secondary text-sm">
              No clients match
            </div>
          ) : (
            filtered.map((c) => {
              const isSelected = selectedIds.has(c.id);
              return (
                <button
                  key={c.id}
                  onClick={() => toggle(c.id)}
                  className={`w-full flex items-center gap-3 px-5 py-3 border-b hover:bg-wa-hover text-left ${
                    isSelected ? 'bg-teal-50' : ''
                  }`}
                  data-testid={`bulk-mb-client-${c.id}`}
                >
                  <div
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                      isSelected
                        ? 'bg-teal-600 border-teal-600 text-white'
                        : 'border-wa-border'
                    }`}
                  >
                    {isSelected && <Check size={14} weight="bold" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="font-semibold text-wa-text-primary truncate">
                        {c.name || 'Customer'}
                      </span>
                      <MBSetupBadge value={c.mb_setup} />
                    </div>
                    <div className="text-xs text-wa-text-secondary truncate">
                      {maskPhone(c.phone, hasFullPhoneAccess)}
                      {c.unread_count > 0 && (
                        <span className="ml-2 inline-flex items-center px-1.5 py-0.5 bg-wa-hover text-red-600 rounded-full text-[10px] font-bold">
                          {c.unread_count} unread
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* Footer — Apply */}
        <div className="border-t px-5 py-3 bg-wa-hover">
          <div className="flex items-center gap-2 mb-2">
            <label className="text-xs font-semibold text-wa-text-secondary">
              Assign to:
            </label>
            <select
              value={targetMB}
              onChange={(e) => setTargetMB(e.target.value)}
              className="flex-1 px-3 py-2 rounded-lg border border-wa-border focus:ring-2 focus:ring-teal-500 outline-none text-sm bg-wa-panel"
              data-testid="bulk-mb-target"
            >
              {MB_SETUPS.map((mb) => (
                <option key={mb} value={mb}>
                  {mb}
                </option>
              ))}
            </select>
            <button
              onClick={() => handleApply(targetMB)}
              disabled={saving || selectedIds.size === 0}
              className="px-4 py-2 rounded-lg bg-teal-600 text-white font-semibold text-sm hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="bulk-mb-apply"
            >
              {saving ? 'Saving...' : `Assign (${selectedIds.size})`}
            </button>
          </div>
          <button
            onClick={() => handleApply(null)}
            disabled={saving || selectedIds.size === 0}
            className="w-full text-xs text-red-600 hover:underline disabled:opacity-40 disabled:no-underline"
            data-testid="bulk-mb-clear-tag"
          >
            Clear MB tag for selected
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkAssignMBModal;
